import * as React from 'react'
import { useOrganization } from '@clerk/clerk-react'
import { useQueryState } from 'nuqs'
import type { RowSelectionState } from '@tanstack/react-table'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { useDataTable } from '@/components/data-table/data-table-provider'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { usePatientsQuery } from '@/hooks/usePatientsQuery'
import type { PatientRecord } from '@/features/patients/types'
import { ImportDataButton } from '@/features/data-import'
import { patientsColumns } from '@/features/patients/components/PatientsColumns'
import { PatientsSheetWrapper } from '@/features/patients/components/PatientsSheetDetails'
import {
  patientsFilterFields,
  patientsSchema,
} from '@/features/patients/components/patientsTableSchema'

const TABLE_ID = 'patients'

/**
 * Sheet slot wrapper: renders the client detail sheet and keeps the `details`
 * URL flag in sync with the sheet's open state — set `details=1` while a row is
 * selected, drop it when selection clears, so the flag never lingers after
 * close. Mirrors Call Logs' sheet slot; the sync reads `rowSelection`, which
 * only exists in the DataTable context DataTableInfinite mounts.
 */
function PatientsSheet() {
  const { rowSelection } = useDataTable<PatientRecord, unknown>()
  const [detailsParam, setDetailsParam] = useQueryState('details', {
    history: 'replace',
  })

  const detailsDesired = Object.keys(rowSelection).length > 0 ? '1' : null
  React.useEffect(() => {
    if (detailsParam !== detailsDesired) void setDetailsParam(detailsDesired)
  }, [detailsDesired, detailsParam, setDetailsParam])

  return <PatientsSheetWrapper />
}

function PatientsInner() {
  const storeState = useFilterState()
  const { resetAllFilters } = useFilterActions()

  // Open the details sheet over a single client when the URL carries
  // `?patients.id=<uuid>&details=1` (the call sidesheet's client-name link).
  // Rows are keyed by patient id (see getRowId) and the id below narrows the
  // query server-side, so the deep-linked row is present to select.
  const [detailsParam] = useQueryState('details')
  const [initialRowSelection] = React.useState<RowSelectionState>(() => {
    const patientId = typeof storeState?.id === 'string' ? storeState.id : null
    return detailsParam === '1' && patientId ? { [patientId]: true } : {}
  })

  // Only real filter fields become query params — exclude store metadata (sort,
  // selected-row uuid) so opening the details sheet doesn't churn the query key.
  const apiFilters = React.useMemo(() => {
    const ids = new Set(patientsFilterFields.map((f) => String(f.value)))
    const columnFilters = Object.entries(storeState ?? {})
      .filter(([key, value]) => {
        if (!ids.has(key)) return false
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        return String(value).trim().length > 0
      })
      .map(([id, value]) => ({ id, value }))
    const conditions = columnFiltersToFilterConditions(columnFilters)

    // `id` is not a user-facing filter field (an internal uuid makes a poor
    // filter chip), so it's appended here rather than via patientsFilterFields:
    // the client-name deep link narrows the table to exactly that client.
    const patientId = typeof storeState?.id === 'string' ? storeState.id : null
    if (patientId) {
      conditions.push({
        id: 'id-=',
        field: 'id',
        operator: '=',
        value: patientId,
      })
    }
    return conditions
  }, [storeState])

  const { organization } = useOrganization()
  const query = usePatientsQuery({
    limit: 50,
    sort_by: 'created_at',
    sort_order: 'desc',
    filters: apiFilters.length > 0 ? apiFilters : undefined,
  })

  const data = React.useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  )
  const totalRows = query.data?.pages[0]?.pagination.total ?? undefined

  const getRowId = React.useCallback((row: PatientRecord) => row.id, [])

  const SheetSlot = React.useMemo(() => <PatientsSheet />, [])

  const ToolbarActions = React.useMemo(
    () => (
      <>
        <ImportDataButton entity="client" entityLabel="Client" />
        <TabRefreshButton
          onRefresh={query.refetch}
          isFetching={query.isFetching}
        />
      </>
    ),
    [query.refetch, query.isFetching]
  )

  return (
    <DataTableInfinite<PatientRecord, unknown>
      tableId={TABLE_ID}
      columns={patientsColumns}
      data={data}
      filterFields={patientsFilterFields}
      manualFiltering
      isRefetching={query.isPlaceholderData}
      isError={query.isError}
      error={query.error}
      entityLabel="clients"
      onClearFilters={resetAllFilters}
      // The query stays disabled until Clerk resolves the org, and a disabled
      // TanStack query reports isFetching=false — so without the org check the
      // table would read "No results." for a beat on a cold load before the
      // first fetch even starts.
      isLoading={query.isLoading || !organization?.id}
      isFetching={query.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRows={totalRows}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      defaultRowSelection={initialRowSelection}
      dataUpdatedAt={query.dataUpdatedAt}
      sheetSlot={SheetSlot}
      toolbarActions={ToolbarActions}
      autoColumnWidth
    />
  )
}

export function PatientsList() {
  const adapter = useNuqsAdapter(patientsSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    // Namespace URL params (`${id}.`) so tables sharing the sidebar's carried
    // location.search don't bleed each other's same-named filters across tabs.
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <PatientsInner />
    </DataTableStoreProvider>
  )
}
